import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import debounce from 'lodash.debounce';
import { Link, Check, X, Ban, Mail } from 'lucide-react';
import toast from 'react-hot-toast';
import ShareProfileEmailDropdown from './ShareProfileEmailDropdown';
import PublicProfileSampleModal from './PublicProfileSampleModal';


interface User {
  id: number;
  username: string;
  public_profile: boolean;
  show_bio_skills: boolean;
  show_hair_makeup_kit: boolean;
  show_wardrobe_kit: boolean;
  show_phone_number: boolean;
  subscription_tier?: string;
}

interface PublicProfileSettingsProps {
  user: User;
  appDomain?: string;
}

const PublicProfileSettings: React.FC<PublicProfileSettingsProps> = ({ user, appDomain }) => {
  const [domainUrl, setDomainUrl] = useState('');
  const [username, setUsername] = useState(user.username || '');
  const [isPublic, setIsPublic] = useState(user.public_profile || false);
  const [showBioSkills, setShowBioSkills] = useState(user.show_bio_skills ?? true);
  const [showHMKit, setShowHMKit] = useState(user.show_hair_makeup_kit || false);
  const [showWardrobeKit, setShowWardrobeKit] = useState(user.show_wardrobe_kit || false);
  const [showPhoneNumber, setShowPhoneNumber] = useState(user.show_phone_number ?? true);

  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isSampleModalOpen, setIsSampleModalOpen] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const [savedStates, setSavedStates] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setDomainUrl(((appDomain || window.location.host).startsWith('http') ? '' : 'https://') + (appDomain || window.location.host) + '/')
  }, [appDomain]);

  const showSavedCheckmark = (key: string) => {
    setSavedStates(prev => ({ ...prev, [key]: true }));
    setTimeout(() => {
      setSavedStates(prev => ({ ...prev, [key]: false }));
    }, 1000);
  };

  const checkUsername = useCallback(
    debounce(async (val: string) => {
      if (!val) {
        setIsAvailable(null);
        setIsValidating(false);
        setValidationError(null);
        return;
      }

      if (val === user.username) {
        setIsAvailable(true);
        setIsValidating(false);
        setValidationError(null);
        return;
      }

      try {
        const response = await axios.get(`/api/v1/users/check_username?username=${val}`);
        setIsAvailable(response.data.available);
        setValidationError(response.data.error || null);
      } catch (err) {
        console.error('Error checking username', err);
        setIsAvailable(null);
      } finally {
        setIsValidating(false);
      }
    }, 500),
    [user.username]
  );

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, '');
    setUsername(val);
    if (val !== user.username) {
      setIsValidating(true);
      setIsAvailable(null);
      checkUsername(val);
    } else {
      setIsAvailable(true);
      setIsValidating(false);
      setValidationError(null);
    }
  };

  const updateSetting = async (key: string, value: any) => {
    try {
      const csrfToken = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content;
      await axios.patch('/auth', {
        user: { [key]: value },
        from_account_page: true
      }, {
        headers: {
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json'
        }
      });
      return true;
    } catch (err) {
      console.error('Error saving settings', err);
      toast.error(`Failed to update setting`);
      return false;
    }
  };

  const togglePublic = async (val: boolean) => {
    setIsPublic(val);
    const success = await updateSetting('public_profile', val);
    if (success) showSavedCheckmark('public_profile');
  };

  const toggleHM = async (val: boolean) => {
    setShowHMKit(val);
    const success = await updateSetting('show_hair_makeup_kit', val);
    if (success) showSavedCheckmark('show_hair_makeup_kit');
  };

  const toggleWardrobe = async (val: boolean) => {
    setShowWardrobeKit(val);
    const success = await updateSetting('show_wardrobe_kit', val);
    if (success) showSavedCheckmark('show_wardrobe_kit');
  };

  const togglePhoneNumber = async (val: boolean) => {
    setShowPhoneNumber(val);
    const success = await updateSetting('show_phone_number', val);
    if (success) showSavedCheckmark('show_phone_number');
  };

  const saveUsername = async () => {
    if (isAvailable && username !== user.username) {
      updateSetting('username', username);
    }
  };

  const handleCopy = () => {
    if (!username) return;
    // const domain = appDomain || window.location.host;
    const url = `${domainUrl}${username}?share_profile=true`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success('Profile URL copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      className="flex flex-col bg-white border border-gray-400 border-opacity-50 mb-0 w-full"
      style={{
        height: isPublic ? 'auto' : '5.5rem',
        padding: '2rem 1.5rem',
        gap: isPublic ? '1.875rem' : '0.625rem',
        borderRadius: '0.625rem',
        maxWidth: '33.125rem'
      }}
    >
      {/* SECTION 1: PUBLIC TOGGLE */}
      <div
        className={`flex flex-col gap-5 ${isPublic ? 'pb-0' : 'pb-0 border-b-0'}`}
      >
        <div className="flex justify-between items-center w-full">
          <span className="font-medium text-sm text-gray-700">
            Make my profile public.
            <button
              type="button"
              onClick={() => setIsSampleModalOpen(true)}
              className="text-blue-500 font-semibold ml-1 hover:underline cursor-pointer"
            >
              See Sample
            </button>
          </span>

          {user.subscription_tier === 'free' ? (
            <a
              href="/user_plans"
              className="flex items-center gap-1 bg-blue-100 border border-blue-500 rounded-full px-2 py-1"
            >
              <p className="ml-auto whitespace-nowrap text-xs sm:text-sm text-gray-900">
                Upgrade to
              </p>


              <span className="flex items-center justify-center bg-gray-900 rounded-full px-2 py-0.5">
                <span className="bg-black text-white font-semibold px-2 py-0.5 rounded-full uppercase sm:text-xs">
                  PRO
                </span>
              </span>
            </a>
          ) : (
            <div className="flex items-center gap-2">
              {savedStates['public_profile'] && <Check size={16} color="#217A00" />}
              <Switch checked={isPublic} onChange={togglePublic} />
            </div>
          )}
        </div>
      </div>

      {isPublic && (
        <>
          {/* SECTION 2: SET USERNAME */}
          <div className="flex flex-col gap-1 w-full">
            <div className="flex justify-between items-center w-full" style={{ height: '1rem' }}>
              <label className="font-medium text-gray-700 tracking-tight" style={{ fontSize: '0.6875rem', lineHeight: '1rem' }}>Set your username</label>
              <div className="flex flex-grow justify-end">
                {validationError && (
                  <span className="font-normal text-red-600 tracking-wider italic" style={{ fontSize: '0.625rem', lineHeight: '130%' }}>{validationError}</span>
                )}
                {!isValidating && isAvailable === false && username !== user.username && !validationError && (
                  <span className="font-normal text-red-600 tracking-wider" style={{ fontSize: '0.625rem', lineHeight: '130%' }}>Username already in use.</span>
                )}
                {!isValidating && isAvailable === true && username !== user.username && (
                  <span className="font-normal text-green-600 tracking-wider" style={{ fontSize: '0.625rem', lineHeight: '130%' }}>Username available</span>
                )}
              </div>
            </div>
            <div
              className="box-border flex items-center bg-white border border-gray-400 border-opacity-50 px-4 py-1 transition-all gap-2"
              style={{ borderRadius: '0.625rem', height: '2.625rem' }}
            >
              <input
                type="text"
                value={username}
                onChange={handleUsernameChange}
                onBlur={saveUsername}
                className="border-none outline-none flex-1 text-sm font-medium p-0 text-gray-700 bg-transparent tracking-wide min-w-0"
                style={{ boxShadow: 'none' }}
                placeholder="username"
              />
              <div className="flex items-center justify-center shrink-0" style={{ width: '1.125rem', height: '1.125rem' }}>
                {isValidating && <div className="pp-spinner" />}
                {!isValidating && isAvailable === true && <Check size={18} color="#217A00" />}
                {!isValidating && isAvailable === false && <Ban size={18} color="#F63C3F" />}
              </div>
            </div>
          </div>

          {/* SECTION 3: PROFILE URL (Separate Input for display/copy) */}
          <div className="flex flex-col gap-1 w-full">
            <div className="flex justify-between items-center w-full" style={{ height: '1rem' }}>
              <label className="font-medium text-gray-700 tracking-tight" style={{ fontSize: '0.6875rem', lineHeight: '1rem' }}>Your shareable profile link</label>
              {emailSent && (
                <span className="font-bold text-green-600 tracking-tight" style={{ fontSize: '0.6875rem', lineHeight: '1rem' }}>Email sent!</span>
              )}
            </div>
            <div
              className={`box-border flex items-center px-4 py-1 transition-all flex-wrap sm:flex-nowrap relative border-2 ${emailSent ? 'border-green-500' : 'border-transparent'}`}
              style={{ paddingRight: '0.25rem', backgroundColor: '#E9EEF6', borderRadius: '0.625rem', minHeight: '2.625rem' }}
            >
              <span className="text-black-400 text-sm font-medium whitespace-nowrap overflow-hidden truncate sm:max-w-none" style={{ maxWidth: '12rem' }}>
                {domainUrl}
              </span>
              <input
                type="text"
                value={username}
                readOnly
                className="border-none outline-none flex-1 text-sm font-medium p-0 text-black-400 bg-transparent tracking-wide truncate"
                style={{ boxShadow: 'none', minWidth: '3rem' }}
              />
              <div className="flex items-center gap-2 ml-auto">
                <button
                  type="button"
                  onClick={handleCopy}
                  className="box-border flex justify-center items-center bg-white border border-gray-400 border-opacity-50 rounded-md cursor-pointer transition-all shrink-0"
                  style={{
                    width: '2.5rem',
                    height: '2.125rem',
                    padding: '0.5rem 0.625rem',
                  }}
                  title="Copy Profile Link"
                >
                  {copied ? <Check size={14} color="#217A00" /> : <Link size={14} color="#374151" />}
                </button>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setIsShareModalOpen(true)}
                    className={`box-border flex justify-center items-center bg-white border ${emailSent ? 'border-green-500' : 'border-gray-400 border-opacity-50'} rounded-md cursor-pointer transition-all shrink-0`}
                    style={{
                      width: '2.5rem',
                      height: '2.125rem',
                      padding: '0.5rem 0.625rem',
                    }}
                    title="Share via Email"
                  >
                    <Mail size={16} color={emailSent ? "#217A00" : "#374151"} />
                  </button>
                </div>
              </div>
              <ShareProfileEmailDropdown
                isOpen={isShareModalOpen}
                onClose={() => setIsShareModalOpen(false)}
                username={username}
                onShareSuccess={() => {
                  setEmailSent(true);
                  setTimeout(() => setEmailSent(false), 5000);
                }}
              />
            </div>
          </div>

          {/* SECTION 4: SHARING OPTIONS */}
          <div className="flex flex-col gap-1 w-full border-t pt-7 border-gray-300 border-opacity-50">
            <p className="font-medium text-gray-700 tracking-tight m-0" style={{ fontSize: '0.6875rem', lineHeight: '1rem' }}>Choose what appears on your shared profile</p>
            <div
              className="box-border flex flex-col justify-center items-start px-6 py-4 w-full bg-white border border-gray-400 border-opacity-50"
              style={{ gap: '0.375rem', borderRadius: '0.625rem' }}
            >
              {/* About Row */}
              <div className="box-border flex flex-row items-center pt-2 w-full" style={{ height: '2.125rem', gap: '0.625rem' }}>
                <span className="font-medium text-sm text-gray-700 tracking-tight flex-1" style={{ lineHeight: '1.3125rem' }}>
                  About: Bio, Skills & Media <span className="italic">(default)</span>
                </span>
                <Check size={18} color="green" />
              </div>
              {/* Phone Number Row */}
              <div className="box-border flex flex-row items-center pt-2 w-full border-t border-gray-300 border-opacity-25" style={{ height: '2.125rem', gap: '0.625rem' }}>
                <span className="font-medium text-sm text-gray-700 tracking-tight flex-1" style={{ lineHeight: '1.3125rem' }}>Phone number</span>
                {savedStates['show_phone_number'] && <Check size={16} color="#217A00" />}
                <Switch checked={showPhoneNumber} onChange={togglePhoneNumber} />
              </div>
              {/* H&M Kit Row */}
              <div className="box-border flex flex-row items-center pt-2 w-full border-t border-gray-300 border-opacity-25" style={{ height: '2.125rem', gap: '0.625rem' }}>
                <span className="font-medium text-sm text-gray-700 tracking-tight flex-1" style={{ lineHeight: '1.3125rem' }}>Hair & Makeup Kit</span>
                {savedStates['show_hair_makeup_kit'] && <Check size={16} color="#217A00" />}
                <Switch checked={showHMKit} onChange={toggleHM} />
              </div>

              {/* Wardrobe Kit Row */}
              <div className="box-border flex flex-row items-center pt-2 w-full border-t border-gray-300 border-opacity-25" style={{ height: '2.125rem', gap: '0.625rem' }}>
                <span className="font-medium text-sm text-gray-700 tracking-tight flex-1" style={{ lineHeight: '1.3125rem' }}>Wardrobe Kit</span>
                {savedStates['show_wardrobe_kit'] && <Check size={16} color="#217A00" />}
                <Switch checked={showWardrobeKit} onChange={toggleWardrobe} />
              </div>
            </div>
          </div>

        </>
      )}

      <PublicProfileSampleModal
        isOpen={isSampleModalOpen}
        onClose={() => setIsSampleModalOpen(false)}
      />

      <style>{`

        .pp-spinner {
          width: 0.875rem;
          height: 0.875rem;
          border: 0.125rem solid rgba(140, 162, 194, 0.2);
          border-top-color: #2563EB;
          border-radius: 50%;
          animation: pp-spin 0.6s linear infinite;
        }
        @keyframes pp-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

const Switch: React.FC<{ checked: boolean, onChange: (val: boolean) => void }> = ({ checked, onChange }) => {
  return (
    <div
      onClick={() => onChange(!checked)}
      className={`flex flex-row items-center w-11 h-6 rounded-full cursor-pointer transition-all ${checked ? 'justify-end bg-blue-500' : 'justify-start bg-gray-200'}`}
      style={{ padding: '0.125rem' }}
    >
      <div
        className="w-5 h-5 bg-white rounded-full transition-all"
        style={{ boxShadow: '0 0.0625rem 0.125rem rgba(0, 0, 0, 0.15)' }}
      />
    </div>
  );
};

export default PublicProfileSettings;
